import { Dropdown, Input, Label, Switch } from '@components';

import { AddToMemberListFormData } from './types';
import useBranchOptions from './useBranchOptions';
import useGroupOptions from './useGroupOptions';
import {
  getMoveToCofeeNameError,
  isMoveToCofeeNameInvalid
} from './validation';

interface AddToMemberListStepProps {
  leadName: string;
  formData: AddToMemberListFormData;
  nameApiError?: string;
  onChange: (data: Partial<AddToMemberListFormData>) => void;
}

const AddToMemberListStep = ({
  leadName,
  formData,
  nameApiError,
  onChange
}: AddToMemberListStepProps) => {
  const branchOptions = useBranchOptions();
  const groupOptions = useGroupOptions(formData.branchId);

  const handleBranchSelect = (value: string) => {
    const branch = branchOptions.find((b) => b.value === value);
    onChange({
      branchId: value,
      branchName: branch?.label || '',
      groupId: '',
      groupName: ''
    });
  };

  const handleGroupSelect = (value: string) => {
    const group = groupOptions.find((g) => g.value === value);
    onChange({
      groupId: value,
      groupName: group?.label || ''
    });
  };

  const showNameError =
    (formData.name.length > 0 && isMoveToCofeeNameInvalid(formData.name)) ||
    !!nameApiError;
  const nameErrorMessage =
    nameApiError || getMoveToCofeeNameError(formData.name);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-cofeeNeutral7">Configure details here:</p>
      <div>
        <Label
          label="Name"
          isRequired
          className="!mb-2 !text-sm !font-semibold"
        />
        <Input
          value={formData.name}
          handleInputChange={(val) => onChange({ name: val })}
          placeholder={leadName}
          inputStyle="bg-white"
          error={showNameError}
          errorMessage={nameErrorMessage}
        />
      </div>
      <div>
        <Label
          label="Branch"
          isRequired
          className="!mb-2 !text-sm !font-semibold"
        />
        <Dropdown
          currentFilter={formData.branchId}
          filterOptions={branchOptions}
          onSelectFilter={handleBranchSelect}
          className="h-12 w-full !rounded-lg"
          optionsStyle="!w-full max-h-56 overflow-y-auto"
          labelStyle="truncate"
          optionItemClassName="break-all"
          placeholder="Select Branch"
          searchBarPlaceholder="Search branch"
          usePortal
        />
      </div>
      <div className="flex items-center gap-2">
        <Switch
          value={formData.assignToGroup}
          handleClick={() =>
            onChange({
              assignToGroup: !formData.assignToGroup,
              groupId: '',
              groupName: ''
            })
          }
        />
        <span className="text-sm font-semibold text-cofeeNeutral13">
          Also assign to a Group
        </span>
      </div>
      {formData.assignToGroup && (
        <div>
          <Label
            label="Group"
            isRequired
            className="!mb-2 !text-sm !font-semibold"
          />
          <Dropdown
            currentFilter={formData.groupId}
            filterOptions={groupOptions}
            onSelectFilter={handleGroupSelect}
            className="h-12 w-full !rounded-lg"
            optionsStyle="!w-full max-h-56 overflow-y-auto"
            labelStyle="truncate"
            optionItemClassName="break-all"
            placeholder="Select Group"
            searchBarPlaceholder="Search group"
            disabled={!formData.branchId}
            usePortal
          />
        </div>
      )}
    </div>
  );
};

export default AddToMemberListStep;
